import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, XCircle, Home, ArrowRight, Heart } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 15;
const POLL_INTERVAL_MS = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('polling'); // polling | paid | failed | expired | error
  const [tx, setTx] = useState(null);
  const pollsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState('error');
      return;
    }
    let stopped = false;
    let timer = null;

    const poll = async () => {
      pollsRef.current += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (stopped) return;
        setTx(data);
        if (data.payment_status === 'paid') {
          setState('paid');
          return;
        }
        if (data.payment_status === 'failed') {
          setState('failed');
          return;
        }
        if (data.payment_status === 'expired') {
          setState('expired');
          return;
        }
        if (pollsRef.current >= MAX_POLLS) {
          setState('error');
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        if (stopped) return;
        if (pollsRef.current >= MAX_POLLS) {
          setState('error');
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };
    poll();
    return () => { stopped = true; if (timer) clearTimeout(timer); };
  }, [sessionId]);

  const amountLabel = tx && tx.amount != null && tx.currency
    ? `${(tx.amount / 100).toFixed(2)} ${tx.currency.toUpperCase()}`
    : null;

  return (
    <section className="min-h-[70vh] flex items-center justify-center px-6 py-20">
      <div className="w-full max-w-lg p-10 rounded-2xl border border-white/10 bg-neutral-900/50 text-center">
        {state === 'polling' && (
          <>
            <div className="mx-auto h-16 w-16 grid place-items-center rounded-full bg-yellow-400/15 text-yellow-400 mb-5">
              <Loader2 size={36} className="animate-spin" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">Vérification du paiement...</h1>
            <p className="mt-3 text-neutral-400">Ne fermez pas cette page. La confirmation arrive dans quelques secondes.</p>
          </>
        )}
        {state === 'paid' && (
          <>
            <div className="mx-auto h-16 w-16 grid place-items-center rounded-full bg-yellow-400/15 text-yellow-400 mb-5">
              <Heart size={32} className="fill-yellow-400" />
            </div>
            <h1 className="text-3xl font-extrabold text-yellow-400">Merci du fond de la ruche 🐝</h1>
            <p className="mt-4 text-neutral-300">Ton soutien de <span className="text-yellow-400 font-semibold">{amountLabel || 'BuzzBump'}</span> est bien reçu. Il compte énormément pour l'avancement du projet.</p>
            <p className="mt-2 text-sm text-neutral-500">Tu vas recevoir un reçu par email de la part de Stripe.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-yellow-400 text-neutral-900 font-semibold hover:bg-yellow-300 transition-colors">
                <Home size={16} /> Retour à l'accueil
              </Link>
              <Link to="/notre-projet" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md border border-white/20 text-white hover:border-yellow-400 hover:text-yellow-400 transition-colors">
                Découvrir le projet <ArrowRight size={16} />
              </Link>
            </div>
          </>
        )}
        {(state === 'failed' || state === 'expired') && (
          <>
            <div className="mx-auto h-16 w-16 grid place-items-center rounded-full bg-red-500/15 text-red-400 mb-5">
              <XCircle size={36} />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">Paiement {state === 'failed' ? 'échoué' : 'expiré'}</h1>
            <p className="mt-3 text-neutral-400">Aucun débit n'a eu lieu. Tu peux réessayer quand tu veux.</p>
            <Link to="/nous-aider" className="mt-8 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-yellow-400 text-neutral-900 font-semibold hover:bg-yellow-300 transition-colors">
              Retourner sur Nous aider
            </Link>
          </>
        )}
        {state === 'error' && (
          <>
            <div className="mx-auto h-16 w-16 grid place-items-center rounded-full bg-orange-500/15 text-orange-400 mb-5">
              <CheckCircle2 size={36} />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">Vérification en cours…</h1>
            <p className="mt-3 text-neutral-400">Le paiement met plus de temps que prévu à se confirmer. Recharge la page dans une minute, ou vérifie tes emails.</p>
            <Link to="/" className="mt-8 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-yellow-400 text-neutral-900 font-semibold hover:bg-yellow-300 transition-colors">
              Retour à l'accueil
            </Link>
          </>
        )}
      </div>
    </section>
  );
}
